import HTMLSerializer from "@convertkit/slate-html-serializer"

const defaultConfig = {
  // Add font-size marks based on the calculated font size
  fontSize: true
}

export default function parse(html, options = {}) {
  const config = { ...defaultConfig, ...options }

  return serializer(config).deserialize(html)
}

const createMark = (type, element, next) => ({
  object: "mark",
  type,
  nodes: next(element.childNodes)
})

const hasTag = (element, tag) => tag === element.tagName.toLowerCase()

/**
 * Normalizes the white space in a DOM element by mutating its
 * `innerHTML`. We want to do this for block elements that will contain
 * text. The browser does not render this white space, along with other
 * white space. More info here:
 * https://medium.com/@patrickbrosset/when-does-white-space-matter-in-html-b90e8a7cdd33
 * The reason we have to follow some of the same rules is that we're
 * parsing this HTML into JSON. If we don't handle it, we'll create
 * text nodes that only contain space and will end up rendering them.
 * @param {DOMElement} element
 */
const normalizeWhitespace = element => {
  element.innerHTML = element.innerHTML.trim()
}

const paragraph = (element, next) => {
  if (hasTag(element, "p")) {
    normalizeWhitespace(element)
    const children = next(element.childNodes)

    /**
     * If there aren't any blocks inside of the paragraph, we're safe to parse
     * normally.
     */
    if (!children.some(child => child.object === "block")) {
      return {
        object: "block",
        type: "paragraph",
        nodes: children
      }
    }

    /**
     * If there are any blocks inside of a paragraph, we need to extract them
     * to the same level as the paragraph. For example, this:
     * <paragraph>
     *   Check out this image:
     *   <image src="example.png"/>
     *   What do you think?
     * </paragraph>
     *
     * should become this:
     * <paragraph>Check out this image:</paragraph>
     * <image src="example.png"/>
     * <paragraph>What do yo think?</paragraph>
     *
     * To do that, we reduce the children to a new array. If the child is
     * a block, we add it to the top level of the array. If it's anything else
     * (inline or text), it belongs inside of a paragraph. So, we either
     * a) append it to the last block if it's a paragraph, or
     * b) if it's not a paragraph, create one, and append it to that.
     */
    return children.reduce((acc, child) => {
      if (child.object === "block") {
        return acc.concat(child)
      }

      if (acc.length === 0 || acc[acc.length - 1].type !== "paragraph") {
        acc.push({
          object: "block",
          type: "paragraph",
          nodes: []
        })
      }

      const paragraph = acc[acc.length - 1]

      paragraph.nodes.push(child)
      return acc
    }, [])
  }
}

const headingNodes = {
  h1: "heading-one",
  h2: "heading-two",
  h3: "heading-three",
  h4: "heading-four",
  h5: "heading-five",
  h6: "heading-six"
}

const headings = (element, next) => {
  const tag = element.tagName.toLowerCase()

  if (headingNodes[tag]) {
    normalizeWhitespace(element)
    return {
      object: "block",
      type: headingNodes[tag],
      nodes: next(element.childNodes)
    }
  }
}

const list = (element, next) => {
  if (hasTag(element, "ol") || hasTag(element, "ul")) {
    const type = hasTag(element, "ol") ? "ordered-list" : "unordered-list"

    return {
      object: "block",
      type,
      nodes: next(element.childNodes)
    }
  } else if (hasTag(element, "li")) {
    normalizeWhitespace(element)

    let nodes = []
    nodes.push({
      object: "block",
      type: "list-item-child",
      nodes: next(element.childNodes)
    })

    // This is for fixing nested lists - we query the <li> element for a nested list
    // and add it as a sibling of `list-item-child`, which is the only way to make the lists work.
    const nested = element.querySelector("ul") || element.querySelector("ol")
    if (nested) {
      const type = hasTag(nested, "ol") ? "ordered-list" : "unordered-list"
      nodes.push({
        object: "block",
        type,
        nodes: next(nested.childNodes)
      })
    }

    return {
      object: "block",
      type: "list-item",
      nodes: nodes
    }
  }
}

const image = (element, next, options) => {
  if (hasTag(element, "img")) {
    const src = element.src
    const alt = element.alt

    return {
      object: "block",
      type: "captioned-image",
      nodes: [
        { object: "block", type: "image", data: { src, alt, ...options } }
      ]
    }
  }
}

const blockquote = (element, next) => {
  if (hasTag(element, "blockquote")) {
    return {
      object: "block",
      type: "blockquote",
      nodes: [
        {
          object: "block",
          type: "blockquote-line",
          nodes: next(element.childNodes)
        }
      ]
    }
  }
}

const code = (element, _next) => {
  if (hasTag(element, "pre")) {
    return {
      object: "block",
      type: "code",
      data: { text: element.textContent }
    }
  }
}

const style = element => {
  if (hasTag(element, "style")) {
    return {
      object: "block",
      type: "style",
      data: { text: element.textContent }
    }
  }
}

const link = (element, next) => {
  if (hasTag(element, "a")) {
    const href = element.href
    let data = { href }

    const triggerId = element.getAttribute("trigger-id")
    if (triggerId) {
      data["triggerId"] = triggerId
      data["existingTrigger"] = true
    }

    const img = element.querySelector("img")

    // If a link contains an image, we're going to make an image node
    // instead of a link node
    if (img) {
      return image(img, next, { href })
    }

    // If an anchor contains another anchor, one will have no text content. Fail gracefully by adding one
    if (!element.textContent) {
      element.textContent = "Button"
    }

    // This is a workaround for parsing links styled as buttons
    // from HTML rendered by editor v1 and v2
    if (
      element.dataset.ckElement === "button" ||
      element.classList.contains("email-button")
    ) {
      return {
        object: "block",
        type: "button",
        data: data,
        nodes: next(element.childNodes)
      }
    }

    return {
      object: "inline",
      type: "link",
      data: data,
      nodes: next(element.childNodes)
    }
  }
}

const bold = (element, next) => {
  if (
    hasTag(element, "b") ||
    hasTag(element, "strong") ||
    (element.style &&
      (element.style.fontWeight >= 700 || element.style.fontWeight === "bold"))
  ) {
    // Google Docs wraps everything in <b>
    if (element.style.fontWeight === "normal") return
    return createMark("bold", element, next)
  }
}

const underline = (element, next) => {
  if (hasTag(element, "u") || element.style?.textDecoration === "underline") {
    return createMark("underline", element, next)
  }
}

const italic = (element, next) => {
  if (
    hasTag(element, "i") ||
    hasTag(element, "em") ||
    element.style?.fontStyle === "italic"
  ) {
    return createMark("italic", element, next)
  }
}

const inlineCode = (element, next) => {
  if (hasTag(element, "code")) {
    return createMark("inline-code", element, next)
  }
}

const fontSize = (element, next) => {
  let size = element.style && element.style.fontSize

  if (!size) return

  if (size.includes("pt")) {
    // Google Docs uses pt, and 1.33 seems like an approximate conversion.
    size = Math.round(parseInt(size) * 1.33)
  } else if (size.includes("em")) {
    // TODO: Handle em/rem when we switch to parsing HTML by loading it into
    // the DOM. For now, we have to ignore it.
    return
  } else {
    size = parseInt(size)
  }

  if (size > 0) {
    return {
      object: "mark",
      type: "font-size",
      data: { fontSize: size },
      nodes: next(element.childNodes)
    }
  }
}

function serializer(config) {
  const types = [
    paragraph,
    headings,
    link,
    list,
    image,
    blockquote,
    code,
    style
  ]

  const marks = [
    bold,
    italic,
    underline,
    inlineCode,
    config.fontSize && fontSize
  ].filter(Boolean)

  const s = new HTMLSerializer({ rules: [] })

  const rules = [
    {
      deserialize(element, next) {
        for (let i = 0; i < types.length; i++) {
          let result = types[i](element, next)

          if (result) return result
        }
      }
    },
    {
      // This rule is copied from slate-html-serializer with
      // with modification. We replace the original rule with this
      // implementation that removes empty text nodes.
      deserialize(el) {
        if (el.tagName && el.tagName.toLowerCase() === "br") {
          return {
            object: "text",
            leaves: [
              {
                object: "leaf",
                text: "\n"
              }
            ]
          }
        }

        if (el.nodeName === "#text") {
          if (el.nodeValue && el.nodeValue.match(/<!--.*?-->/)) return

          const text = el.nodeValue.replace(/\s+/g, " ")

          // Remove empty text nodes, unless the parent is a `span`.
          // This is an approximation of how the browser handles white space in HTML.
          // We do not want to create new nodes for white space, unless that white
          // space would render in HTML.
          if (text.trim() === "" && el.parentElement.nodeName !== "SPAN") {
            return
          }

          return {
            object: "text",
            leaves: [
              {
                object: "leaf",
                text
              }
            ]
          }
        }
      }
    },
    {
      deserialize(element, next) {
        const mark = marks.reduce((mark, fn) => {
          const childMark = fn(element, next)

          if (childMark) {
            next = () => s.deserializeMark(childMark)
            return childMark
          }
          return mark
        }, undefined)

        if (mark) return mark
      }
    }
  ]

  const serializer = new HTMLSerializer({
    rules
  })

  // Remove the default slate-html-serializer text rule that we
  // replaced above. This is highly implementation dependent on
  // slate-html-serializer, but our tests should catch any
  // regressions.
  serializer.rules.pop()

  return serializer
}
